/**
 * WRO Claims Dialog
 *
 * Completed WROs that received fewer units than expected are money owed back.
 * The list shows every shortfall (claimed ones badged); picking one opens a
 * reviewable claim email to ShipBob — WRO number, missing-unit table, credit
 * total — with the manufacturer invoice + packing list attached. Nothing sends
 * until the Send button.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, Tooltip } from "@mui/material";
import moment from "moment";
import InventoryService, { WroClaim, WroShortfall } from "services/InventoryService";
import { shipbobWroUrl } from "shared/utils/shipbobLinks";

const OPEN_CLAIM_STATUSES = ["sent", "info_requested"];
const STALE_AFTER_DAYS = 7;

export const daysSince = (timestamp: string): number =>
  Math.max(0, moment().diff(moment(timestamp), "days"));

/** Open claims with no activity for a week need a follow-up nudge. */
export const claimNeedsFollowUp = (claim: WroClaim): boolean =>
  OPEN_CLAIM_STATUSES.includes(claim.status) && daysSince(claim.lastActivityAt) >= STALE_AFTER_DAYS;

const DEFAULT_TO = "support@shipbob.com, jpopoff@shipbob.com";
// Sends go out via Mailgun, not the sender's mailbox — cc'ing keeps a copy in their inbox
const DEFAULT_CC = "inno@sbly.com";


const money = (value: number) =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const parseEmails = (value: string): string[] =>
  value.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);

export const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function buildClaimBody(wro: WroShortfall): string {
  const lines = wro.lines.map(line => {
    const label = `${line.name || line.sku || `Inventory ${line.inventoryId}`} (inventory ID ${line.inventoryId})`;
    const math = line.credit != null
      ? ` × ${money(line.unitPrice as number)} = ${money(line.credit)}`
      : " (unit cost per attached invoice)";
    return `- ${label}: expected ${line.expected}, received ${line.received} — missing ${line.missing}${math}`;
  }).join("\n");
  const total = wro.creditTotal != null
    ? `Total credit requested: ${money(wro.creditTotal)}`
    : "Total credit requested per the attached invoice pricing.";
  return (
    `Hi ShipBob team,\n\n` +
    `WRO ${wro.wroId}${wro.purchaseOrderNumber ? ` (PO ${wro.purchaseOrderNumber})` : ""} was completed short of the expected quantities. ` +
    `We're requesting a credit for the missing units:\n\n${lines}\n\n${total}\n\n` +
    `The manufacturer invoice and packing list for this PO are attached.\n\nThanks,\nInno`
  );
}

interface WroClaimsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Shortfalls were handled — a claim was emailed, or they were dismissed */
  onResolved: (summary: string, resolvedCount: number) => void;
}

export default function WroClaimsDialog({ open, onClose, onResolved }: WroClaimsDialogProps): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [shortfalls, setShortfalls] = useState<WroShortfall[] | null>(null);
  const [claiming, setClaiming] = useState<WroShortfall | null>(null);
  const [to, setTo] = useState(DEFAULT_TO);
  const [cc, setCc] = useState(DEFAULT_CC);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [tab, setTab] = useState<"shortfalls" | "claims">("shortfalls");
  // Claim lifecycle: resolve (credit/denied/info) + follow-up email targets
  const [resolving, setResolving] = useState<WroShortfall | null>(null);
  const [resolveStatus, setResolveStatus] = useState<"credited" | "denied" | "info_requested">("credited");
  const [resolveAmount, setResolveAmount] = useState("");
  const [resolveNote, setResolveNote] = useState("");
  const [isResolving, setIsResolving] = useState(false);
  const [followingUp, setFollowingUp] = useState<WroShortfall | null>(null);
  const [followUpBody, setFollowUpBody] = useState("");
  const [isFollowingUp, setIsFollowingUp] = useState(false);
  // Dismiss flow: which WROs are being dismissed (one or many), sharing a note
  const [dismissing, setDismissing] = useState<WroShortfall[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dismissNote, setDismissNote] = useState("");
  const [isDismissing, setIsDismissing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClaiming(null);
    setSelectedIds(new Set());
    setResolving(null);
    setFollowingUp(null);
    setErrorMessage(null);
    service.getWroShortfalls().then(response => {
      if (response) setShortfalls(response.shortfalls);
      else setErrorMessage("Failed to load WRO shortfalls");
    });
  }, [open, service]);

  const startClaim = (wro: WroShortfall) => {
    setClaiming(wro);
    setTo(DEFAULT_TO);
    setCc(DEFAULT_CC);
    setSubject(`Missing units credit request — WRO ${wro.wroId}${wro.purchaseOrderNumber ? ` (PO ${wro.purchaseOrderNumber})` : ""}`);
    setBody(buildClaimBody(wro));
    setFiles([]);
    setErrorMessage(null);
  };

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    setFiles(prev => [...prev, ...Array.from(fileList)].slice(0, 5));
  };

  const toEmails = parseEmails(to);
  const ccEmails = parseEmails(cc);
  const invalidEmails = [...toEmails, ...ccEmails].filter(e => !isValidEmail(e));
  const isFormValid = claiming != null && toEmails.length > 0 && invalidEmails.length === 0
    && subject.trim().length > 0 && body.trim().length > 0 && files.length > 0;

  const handleSend = async () => {
    if (!isFormValid || !claiming) return;
    setIsSending(true);
    setErrorMessage(null);
    try {
      const result = await service.sendWroClaim({
        wroId: claiming.wroId,
        purchaseOrderNumber: claiming.purchaseOrderNumber,
        to: toEmails,
        cc: ccEmails,
        subject: subject.trim(),
        body,
        lines: claiming.lines,
        files,
      });
      onResolved(
        `Claim sent for WRO ${claiming.wroId}` +
        (result.creditTotal != null ? ` — ${money(result.creditTotal)} requested` : ""),
        1
      );
      onClose();
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleDismiss = async () => {
    if (!dismissing || dismissing.length === 0) return;
    setIsDismissing(true);
    setErrorMessage(null);
    try {
      await service.dismissWroShortfalls({
        wros: dismissing.map(wro => ({ wroId: wro.wroId, purchaseOrderNumber: wro.purchaseOrderNumber })),
        note: dismissNote.trim() || undefined,
      });
      // Reflect it in place so the list doesn't need a refetch
      const dismissedIds = new Set(dismissing.map(wro => wro.wroId));
      // Placeholder claim until the next fetch returns the real row (id -1 is
      // never sent anywhere — dismissed rows have no lifecycle actions).
      const now = new Date().toISOString();
      setShortfalls(prev => (prev || []).map(wro =>
        dismissedIds.has(wro.wroId)
          ? {
              ...wro,
              claim: {
                id: -1, status: "dismissed", creditAmount: null, creditedAmount: null,
                note: dismissNote.trim() || null, sentTo: null, subject: null,
                claimedAt: now, lastActivityAt: now, resolvedAt: null,
              },
            }
          : wro
      ));
      setSelectedIds(prev => new Set(Array.from(prev).filter(id => !dismissedIds.has(id))));
      onResolved(
        dismissing.length === 1
          ? `Dismissed WRO ${dismissing[0].wroId}`
          : `Dismissed ${dismissing.length} WROs`,
        dismissing.length
      );
      setDismissing(null);
      setDismissNote("");
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsDismissing(false);
    }
  };

  const applyClaimUpdate = (wroId: string, patch: Partial<WroClaim>) => {
    setShortfalls(prev => (prev || []).map(wro =>
      wro.wroId === wroId && wro.claim ? { ...wro, claim: { ...wro.claim, ...patch } } : wro
    ));
  };

  const handleResolve = async () => {
    if (!resolving?.claim) return;
    const amount = parseFloat(resolveAmount);
    if (resolveStatus === "credited" && (isNaN(amount) || amount < 0)) {
      setErrorMessage("Enter the credited amount");
      return;
    }
    setIsResolving(true);
    setErrorMessage(null);
    try {
      await service.updateWroClaim(resolving.claim.id, {
        status: resolveStatus,
        creditedAmount: resolveStatus === "credited" ? amount : null,
        note: resolveNote.trim() || undefined,
      });
      applyClaimUpdate(resolving.wroId, {
        status: resolveStatus,
        creditedAmount: resolveStatus === "credited" ? amount : null,
        lastActivityAt: new Date().toISOString(),
        resolvedAt: resolveStatus === "info_requested" ? null : new Date().toISOString(),
      });
      onResolved(
        resolveStatus === "credited"
          ? `WRO ${resolving.wroId}: ${money(amount)} credit recorded`
          : resolveStatus === "denied"
            ? `WRO ${resolving.wroId}: claim marked denied`
            : `WRO ${resolving.wroId}: marked info requested`,
        0
      );
      setResolving(null);
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsResolving(false);
    }
  };

  const startFollowUp = (wro: WroShortfall) => {
    if (!wro.claim) return;
    setFollowingUp(wro);
    setTo(wro.claim.sentTo || DEFAULT_TO);
    setCc(DEFAULT_CC);
    setSubject(`Re: ${wro.claim.subject || `Missing units credit request — WRO ${wro.wroId}`}`);
    setFollowUpBody(
      `Hi ShipBob team,\n\nFollowing up on our credit request for WRO ${wro.wroId}` +
      `${wro.purchaseOrderNumber ? ` (PO ${wro.purchaseOrderNumber})` : ""}, sent on ` +
      `${moment(wro.claim.claimedAt).format("M/D/YYYY")} — could you share an update on its status?` +
      `\n\nThanks,\nInno`
    );
    setErrorMessage(null);
  };

  const handleFollowUp = async () => {
    if (!followingUp?.claim) return;
    const toList = parseEmails(to);
    const ccList = parseEmails(cc);
    if (toList.length === 0 || [...toList, ...ccList].some(e => !isValidEmail(e))) {
      setErrorMessage("Check the recipient addresses");
      return;
    }
    setIsFollowingUp(true);
    setErrorMessage(null);
    try {
      await service.sendWroClaimFollowUp(followingUp.claim.id, {
        to: toList,
        cc: ccList,
        subject: subject.trim(),
        body: followUpBody,
      });
      applyClaimUpdate(followingUp.wroId, { lastActivityAt: new Date().toISOString() });
      onResolved(`Follow-up sent for WRO ${followingUp.wroId}`, 0);
      setFollowingUp(null);
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsFollowingUp(false);
    }
  };

  const renderList = () => {
    if (shortfalls === null) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4, justifyContent: "center" }}>
          <CircularProgress size={20} />
          <Typography>Checking completed WROs…</Typography>
        </Box>
      );
    }
    if (shortfalls.length === 0) {
      return <Alert severity="success">Every completed WRO received its full expected quantities.</Alert>;
    }
    const unclaimed = shortfalls.filter(wro => !wro.claim);
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={unclaimed.length > 0 && selectedIds.size === unclaimed.length}
                indeterminate={selectedIds.size > 0 && selectedIds.size < unclaimed.length}
                onChange={(e) => setSelectedIds(e.target.checked
                  ? new Set(unclaimed.map(wro => wro.wroId))
                  : new Set())}
                disabled={unclaimed.length === 0}
              />
            </TableCell>
            <TableCell>WRO</TableCell>
            <TableCell>PO</TableCell>
            <TableCell align="right">Missing units</TableCell>
            <TableCell align="right">Credit</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {shortfalls.map(wro => (
            <TableRow key={wro.wroId} hover selected={selectedIds.has(wro.wroId)}>
              <TableCell padding="checkbox">
                {!wro.claim && (
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(wro.wroId)}
                    onChange={(e) => setSelectedIds(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(wro.wroId);
                      else next.delete(wro.wroId);
                      return next;
                    })}
                  />
                )}
              </TableCell>
              <TableCell>
                <Link href={shipbobWroUrl(wro.wroId)} target="_blank" rel="noopener" underline="hover">
                  {wro.wroId}
                </Link>
              </TableCell>
              <TableCell sx={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {wro.purchaseOrderNumber || "—"}
              </TableCell>
              <TableCell align="right">{wro.missingUnits.toLocaleString()}</TableCell>
              <TableCell align="right">{wro.creditTotal != null ? money(wro.creditTotal) : "—"}</TableCell>
              <TableCell align="right">
                {wro.claim ? (
                  <Tooltip title={wro.claim.note || ""}>
                    <Chip
                      size="small"
                      label={wro.claim.status === "dismissed" ? "Dismissed" : `Claim ${wro.claim.status}`}
                      color="default"
                    />
                  </Tooltip>
                ) : (
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                    <Button size="small" variant="outlined" onClick={() => startClaim(wro)}>
                      File claim…
                    </Button>
                    <Tooltip title="Already handled — filed by hand, or the cause is known">
                      <Button size="small" color="inherit" onClick={() => { setDismissing([wro]); setDismissNote(""); }}>
                        Dismiss
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderClaim = (wro: WroShortfall) => (
    <>
      <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => setClaiming(null)} sx={{ mb: 1 }}>
        Back to shortfalls
      </Button>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right">Expected</TableCell>
            <TableCell align="right">Received</TableCell>
            <TableCell align="right">Missing</TableCell>
            <TableCell align="right">Credit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {wro.lines.map(line => (
            <TableRow key={`${line.inventoryId}|${line.poNumber ?? ""}`}>
              <TableCell>
                {line.name || line.sku || `Inventory ${line.inventoryId}`}
                {wro.poNumbers.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {line.poNumber ? `${line.poNumber}'s share` : "not on any PO"}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">{line.expected.toLocaleString()}</TableCell>
              <TableCell align="right">{line.received.toLocaleString()}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{line.missing.toLocaleString()}</TableCell>
              <TableCell align="right">{line.credit != null ? money(line.credit) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)}
          helperText="Separate multiple addresses with commas" />
        <TextField size="small" label="Cc" value={cc} onChange={(e) => setCc(e.target.value)} />
        <TextField size="small" label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <TextField label="Message" value={body} onChange={(e) => setBody(e.target.value)} multiline minRows={8} />

        <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setIsDragOver(false); addFiles(e.dataTransfer.files); }}
          sx={{
            border: "2px dashed",
            borderColor: isDragOver ? "primary.main" : "#CBD5E1",
            backgroundColor: isDragOver ? "#EEF2FF" : "#F8FAFC",
            borderRadius: 2, p: 2, textAlign: "center", cursor: "pointer",
          }}
        >
          <AttachFileIcon sx={{ color: "#64748B" }} fontSize="small" />
          <Typography variant="body2">
            Drop the manufacturer invoice and packing list here (or click) — required for the claim
          </Typography>
          {files.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center", mt: 1 }}>
              {files.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  size="small"
                  label={file.name}
                  onDelete={(e: any) => { e.stopPropagation?.(); setFiles(prev => prev.filter((_, i) => i !== index)); }}
                />
              ))}
            </Box>
          )}
        </Box>
        {invalidEmails.length > 0 && (
          <Alert severity="warning">Not a valid email: {invalidEmails.join(", ")}</Alert>
        )}
      </Box>
    </>
  );

  const claimRows = (shortfalls || []).filter(wro => wro.claim && wro.claim.status !== "dismissed");
  const openClaimCount = claimRows.filter(wro => OPEN_CLAIM_STATUSES.includes(wro.claim!.status)).length;

  const renderClaims = () => {
    if (claimRows.length === 0) {
      return <Alert severity="info">No claims filed yet — file one from the Shortfalls tab.</Alert>;
    }
    // Open claims first, stalest on top; resolved ones sink to the bottom
    const sorted = [...claimRows].sort((a, b) => {
      const aOpen = OPEN_CLAIM_STATUSES.includes(a.claim!.status) ? 0 : 1;
      const bOpen = OPEN_CLAIM_STATUSES.includes(b.claim!.status) ? 0 : 1;
      return aOpen - bOpen || daysSince(b.claim!.lastActivityAt) - daysSince(a.claim!.lastActivityAt);
    });
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>WRO</TableCell>
            <TableCell>PO</TableCell>
            <TableCell align="right">Requested</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last activity</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(wro => {
            const claim = wro.claim!;
            const isOpen = OPEN_CLAIM_STATUSES.includes(claim.status);
            const quietDays = daysSince(claim.lastActivityAt);
            return (
              <TableRow key={wro.wroId} hover>
                <TableCell>
                  <Link href={shipbobWroUrl(wro.wroId)} target="_blank" rel="noopener" underline="hover">
                    {wro.wroId}
                  </Link>
                </TableCell>
                <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {wro.purchaseOrderNumber || "—"}
                </TableCell>
                <TableCell align="right">{claim.creditAmount != null ? money(claim.creditAmount) : "—"}</TableCell>
                <TableCell>
                  <Tooltip title={claim.note || ""}>
                    <Chip
                      size="small"
                      label={
                        claim.status === "credited"
                          ? `Credited ${claim.creditedAmount != null ? money(claim.creditedAmount) : ""}`
                          : claim.status === "denied"
                            ? "Denied"
                            : claim.status === "info_requested"
                              ? "Info requested"
                              : "Awaiting ShipBob"
                      }
                      color={claim.status === "credited" ? "success" : claim.status === "denied" ? "default" : claimNeedsFollowUp(claim) ? "warning" : "default"}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={isOpen && quietDays >= STALE_AFTER_DAYS ? "warning.main" : "text.secondary"}>
                    {quietDays === 0 ? "today" : `${quietDays}d ago`}
                    {isOpen && quietDays >= STALE_AFTER_DAYS ? " — follow up" : ""}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {isOpen && (
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                      <Button size="small" onClick={() => startFollowUp(wro)}>Follow up…</Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setResolving(wro);
                          setResolveStatus("credited");
                          setResolveAmount(claim.creditAmount != null ? String(claim.creditAmount) : "");
                          setResolveNote("");
                        }}
                      >
                        Resolve…
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog open={open} onClose={isSending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {claiming
          ? `Credit claim — WRO ${claiming.wroId}`
          : "Missing units on completed WROs"}
      </DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}
        {!claiming && (
          <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
            <Tab value="shortfalls" label="Shortfalls" />
            <Tab value="claims" label={`Claims${openClaimCount > 0 ? ` (${openClaimCount} open)` : ""}`} />
          </Tabs>
        )}
        {claiming ? renderClaim(claiming) : tab === "claims" ? renderClaims() : renderList()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>Close</Button>
        {!claiming && selectedIds.size > 0 && (
          <Button
            variant="outlined"
            onClick={() => {
              setDismissing((shortfalls || []).filter(wro => selectedIds.has(wro.wroId) && !wro.claim));
              setDismissNote("");
            }}
          >
            Dismiss selected ({selectedIds.size})…
          </Button>
        )}
        {claiming && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={!isFormValid || isSending}
          >
            {isSending ? "Sending…" : "Send claim"}
          </Button>
        )}
      </DialogActions>

      <Dialog open={dismissing != null} onClose={isDismissing ? undefined : () => setDismissing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dismissing?.length === 1
            ? `Dismiss WRO ${dismissing[0].wroId}?`
            : `Dismiss ${dismissing?.length ?? 0} WROs?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stops flagging {dismissing && dismissing.length === 1 ? "this shortfall" : "these shortfalls"} — no
            email is sent. Use it when the claim was already filed outside the
            tool or the cause is known.
          </Typography>
          {dismissing && dismissing.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              WROs: {dismissing.map(wro => wro.wroId).join(", ")}
            </Typography>
          )}
          <TextField
            size="small"
            fullWidth
            label={dismissing && dismissing.length > 1 ? "Note (optional, applied to all)" : "Note (optional)"}
            placeholder="e.g. claim filed by email on 8/12, ticket #12345"
            value={dismissNote}
            onChange={(e) => setDismissNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissing(null)} disabled={isDismissing}>Cancel</Button>
          <Button variant="contained" onClick={handleDismiss} disabled={isDismissing}>
            {isDismissing ? "Dismissing…" : "Dismiss"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resolving != null} onClose={isResolving ? undefined : () => setResolving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Resolve claim — WRO {resolving?.wroId}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              select
              size="small"
              label="Outcome"
              value={resolveStatus}
              onChange={(e) => setResolveStatus(e.target.value as any)}
              SelectProps={{ native: true }}
            >
              <option value="credited">Credit received</option>
              <option value="denied">Denied</option>
              <option value="info_requested">ShipBob asked for more info (stays open)</option>
            </TextField>
            {resolveStatus === "credited" && (
              <TextField
                size="small"
                type="number"
                label="Credited amount ($)"
                value={resolveAmount}
                onChange={(e) => setResolveAmount(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                helperText={resolving?.claim?.creditAmount != null
                  ? `Requested ${money(resolving.claim.creditAmount)}`
                  : undefined}
              />
            )}
            <TextField
              size="small"
              label="Note (optional)"
              placeholder="e.g. confirmed by ShipBob email 9/10"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolving(null)} disabled={isResolving}>Cancel</Button>
          <Button variant="contained" onClick={handleResolve} disabled={isResolving}>
            {isResolving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={followingUp != null} onClose={isFollowingUp ? undefined : () => setFollowingUp(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Follow up — WRO {followingUp?.wroId}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField size="small" label="To" value={to} onChange={(e) => setTo(e.target.value)} />
            <TextField size="small" label="Cc" value={cc} onChange={(e) => setCc(e.target.value)} />
            <TextField size="small" label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            <TextField label="Message" value={followUpBody} onChange={(e) => setFollowUpBody(e.target.value)} multiline minRows={5} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowingUp(null)} disabled={isFollowingUp}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={handleFollowUp} disabled={isFollowingUp}>
            {isFollowingUp ? "Sending…" : "Send follow-up"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
